import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Check,
  Copy,
  Download,
  ExternalLink,
  House,
  Link2,
  QrCode as QrCodeIcon,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useAuthSession } from '../../../contexts/AuthSessionContext';
import QrCodeCanvas from './components/QrCodeCanvas';
import { CHANNELS } from './constants';
import {
  loadAtendabotWhatsapp,
  loadGoogleReviewLink,
  loadRestaurantInfo,
} from './services/api';

// Paleta clara — visual clean e amigável.
const C = {
  text: '#0f172a',
  strong: '#0b1220',
  muted: '#64748b',
  soft: '#94a3b8',
  accent: '#10b981',
  accentDeep: '#0f9d63',
  teal: '#0ea5e9',
  card: '#ffffff',
  border: 'rgba(15,23,42,0.09)',
  inset: '#f3f6fb',
  insetBorder: 'rgba(15,23,42,0.08)',
};

const cardSx = {
  position: 'relative',
  borderRadius: '20px',
  border: `1px solid ${C.border}`,
  background: C.card,
  boxShadow: '0 10px 30px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
  p: { xs: 2, md: 2.5 },
};

const iconBtnSx = {
  width: 38,
  height: 38,
  borderRadius: '11px',
  color: C.muted,
  border: `1px solid ${C.border}`,
  background: '#fff',
  transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    background: 'rgba(16,185,129,0.10)',
    borderColor: 'rgba(16,185,129,0.42)',
    color: C.accentDeep,
  },
};

const ChannelAvatar = ({ channel, size = 58 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '13px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: channel.icon ? channel.backgroundColor : `${channel.accent}1f`,
      border: `1px solid ${C.border}`,
      boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
    }}
  >
    {channel.icon ? (
      // Mesmo tratamento do LinkPage: a logo preenche o tile (contain + padding).
      <img
        src={channel.icon}
        alt={channel.name}
        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }}
      />
    ) : (
      <Box component="span" sx={{ fontSize: size * 0.48, lineHeight: 1 }}>
        {channel.emoji || '🔗'}
      </Box>
    )}
  </Box>
);

const prettyUrl = (url = '') => url.replace(/^https?:\/\//, '');

const AtivoBadge = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.9,
      py: 0.28,
      borderRadius: '999px',
      background: 'rgba(16,185,129,0.10)',
      border: '1px solid rgba(16,185,129,0.30)',
      color: C.accentDeep,
      fontSize: '0.68rem',
      fontWeight: 700,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    }}
  >
    <ShieldCheck size={11} />
    Ativo
  </Box>
);

const ChannelCard = ({ channel, copied, onCopy, onOpenQr }) => (
  <Box
    sx={{
      ...cardSx,
      transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        borderColor: 'rgba(16,185,129,0.35)',
        boxShadow: '0 16px 38px rgba(15,23,42,0.10)',
      },
    }}
  >
    {/* Cabeçalho: ícone + nome/descrição à esquerda, "Ativo" no canto superior direito */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <ChannelAvatar channel={channel} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: C.strong, lineHeight: 1.25 }}>
          {channel.name}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: C.muted }}>
          {channel.description}
        </Typography>
      </Box>
      <AtivoBadge />
    </Box>

    {/* URL clicável (abre a página) + ações QR / copiar */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75 }}>
      <Tooltip title="Abrir página">
        <Box
          component="a"
          href={channel.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 0,
            px: 1.5,
            py: 1,
            borderRadius: '12px',
            textDecoration: 'none',
            background: C.inset,
            border: `1px solid ${C.insetBorder}`,
            transition: 'border-color 0.2s ease, background 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(16,185,129,0.4)',
              background: '#eef4fb',
              '& .ml-open': { opacity: 1 },
            },
          }}
        >
          <Link2 size={14} color={C.accentDeep} style={{ flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.82rem',
              color: '#334155',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {prettyUrl(channel.url)}
          </Typography>
          <ExternalLink
            className="ml-open"
            size={14}
            color={C.soft}
            style={{ flexShrink: 0, opacity: 0, transition: 'opacity 0.2s ease' }}
          />
        </Box>
      </Tooltip>

      <Tooltip title="Ver / baixar QR Code">
        <IconButton onClick={() => onOpenQr(channel)} aria-label={`QR Code de ${channel.name}`} sx={iconBtnSx}>
          <QrCodeIcon size={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title={copied ? 'Copiado!' : 'Copiar link'}>
        <IconButton
          onClick={() => onCopy(channel)}
          aria-label={`Copiar link de ${channel.name}`}
          sx={{
            ...iconBtnSx,
            ...(copied
              ? { color: C.accentDeep, borderColor: 'rgba(16,185,129,0.5)', background: 'rgba(16,185,129,0.12)' }
              : {}),
          }}
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

const SkeletonCard = () => (
  <Box sx={{ ...cardSx, '@keyframes mlPulse': { '0%,100%': { opacity: 0.55 }, '50%': { opacity: 0.85 } } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, animation: 'mlPulse 1.6s ease-in-out infinite' }}>
      <Box sx={{ width: 58, height: 58, borderRadius: '13px', background: '#e9eef6' }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ width: '45%', height: 12, borderRadius: 999, background: '#e5eaf2', mb: 1 }} />
        <Box sx={{ width: '65%', height: 10, borderRadius: 999, background: '#eef2f8' }} />
      </Box>
    </Box>
    <Box sx={{ height: 40, borderRadius: '12px', background: '#eef2f8', mt: 1.75, animation: 'mlPulse 1.6s ease-in-out infinite' }} />
  </Box>
);

const MyLinks = () => {
  const navigate = useNavigate();
  const { hasProductAccess } = useAuthSession();
  const qrRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null); // { id, slug, name }
  const [whatsapp, setWhatsapp] = useState('');
  const [googleLink, setGoogleLink] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [qrChannel, setQrChannel] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const info = await loadRestaurantInfo();
        if (!active) return;
        setRestaurant(info);
        const extras = [];
        if (hasProductAccess('atendabot', { allowTrialRestricted: true })) {
          extras.push(loadAtendabotWhatsapp().then((p) => active && setWhatsapp(p)));
        }
        if (hasProductAccess('feedback_hero', { allowTrialRestricted: true })) {
          extras.push(loadGoogleReviewLink().then((g) => active && setGoogleLink(g)));
        }
        await Promise.allSettled(extras);
      } catch (err) {
        console.error('Erro ao carregar dados do restaurante:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // Carrega dados do restaurante só no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Canais habilitados (produto contratado) com link real montado a partir do slug.
  const channels = useMemo(() => {
    const origin =
      typeof window !== 'undefined'
        ? String(window.location.origin).replace(/\/+$/, '')
        : '';
    const ctx = {
      origin,
      slug: restaurant?.slug || '',
      restaurantId: restaurant?.id || '',
      whatsapp,
      googleLink,
    };
    return CHANNELS.filter((c) =>
      hasProductAccess(c.productKey, { allowTrialRestricted: true })
    )
      .map((c) => ({ ...c, url: c.build(ctx) }))
      .filter((c) => Boolean(c.url));
  }, [restaurant, whatsapp, googleLink, hasProductAccess]);

  const copyUrl = async (url) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const el = document.createElement('textarea');
        el.value = url;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleCopy = async (channel) => {
    const ok = await copyUrl(channel.url);
    if (ok) {
      setCopiedId(channel.id);
      toast.success('Link copiado!');
      setTimeout(() => setCopiedId((cur) => (cur === channel.id ? null : cur)), 2200);
    } else {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleDownloadQr = () => {
    if (!qrChannel) return;
    const ok = qrRef.current?.download(`qr-${qrChannel.id}.png`);
    if (ok) toast.success('QR Code baixado!');
    else toast.error('Não foi possível gerar o QR Code.');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        boxSizing: 'border-box',
        px: { xs: 2.5, md: 6 },
        py: { xs: 4, md: 'clamp(40px, 7vh, 64px)' },
        background: `
          radial-gradient(circle at 12% 8%, rgba(16,185,129,0.10), transparent 30%),
          radial-gradient(circle at 88% 4%, rgba(14,165,233,0.09), transparent 28%),
          linear-gradient(170deg, #f7fafc 0%, #eef3f9 100%)
        `,
        color: C.text,
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', maxWidth: '1080px', mx: 'auto' }}>
        {/* Botão redondo "casa" — voltar para produtos */}
        <Box sx={{ mb: { xs: 3, md: 3.5 } }}>
          <Tooltip title="Voltar para produtos">
            <IconButton
              onClick={() => navigate('/products')}
              aria-label="Voltar para produtos"
              sx={{
                width: 46,
                height: 46,
                color: C.strong,
                border: `1px solid ${C.border}`,
                background: '#fff',
                boxShadow: '0 4px 14px rgba(15,23,42,0.08)',
                transition: 'all .2s ease',
                '&:hover': {
                  background: 'rgba(16,185,129,0.10)',
                  borderColor: 'rgba(16,185,129,0.42)',
                  color: C.accentDeep,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <House size={22} strokeWidth={2.2} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Cabeçalho */}
        <Box sx={{ mb: { xs: 3.5, md: 4.5 }, maxWidth: 640 }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.15rem', md: '3rem' },
              lineHeight: 1.0,
              letterSpacing: '-0.03em',
              color: C.strong,
              fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
            }}
          >
            Meus{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(120deg, #10b981 0%, #14b8a6 45%, #0ea5e9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Links
            </Box>
          </Typography>

          <Typography
            sx={{
              mt: { xs: 2, md: 2.25 },
              color: C.muted,
              fontSize: { xs: '1.02rem', md: '1.14rem' },
              lineHeight: 1.65,
            }}
          >
            Um{' '}
            <Box component="span" sx={{ color: C.strong, fontWeight: 600 }}>
              link direto
            </Box>{' '}
            e um{' '}
            <Box component="span" sx={{ color: C.accentDeep, fontWeight: 600 }}>
              QR Code
            </Box>{' '}
            para cada canal do seu restaurante. Prontos para compartilhar nas redes ou
            imprimir nas mesas, no balcão e na vitrine.
          </Typography>
        </Box>

        {/* Conteúdo: skeleton → grade de canais → estado vazio */}
        {loading ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1.75, md: 2 },
            }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </Box>
        ) : channels.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1.75, md: 2 },
            }}
          >
            {channels.map((channel) => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                copied={copiedId === channel.id}
                onCopy={handleCopy}
                onOpenQr={setQrChannel}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ ...cardSx, textAlign: 'center', py: 5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: 'auto',
                mb: 2,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(16,185,129,0.10)',
                border: '1px solid rgba(16,185,129,0.24)',
                color: C.accentDeep,
              }}
            >
              <Link2 size={24} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: C.strong, mb: 0.75 }}>
              Nenhum canal disponível ainda
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: C.muted, maxWidth: 420, mx: 'auto' }}>
              Ative produtos como One Menu, Reservo ou Feedback Hero e finalize a
              configuração do seu restaurante para gerar os links públicos.
            </Typography>
            <Button
              onClick={() => navigate('/products')}
              sx={{
                mt: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '12px',
                px: 2.5,
                py: 1,
                color: '#fff',
                background: 'linear-gradient(135deg, #10b981, #0ea5e9)',
                boxShadow: '0 12px 26px rgba(16,185,129,0.28)',
                '&:hover': { background: 'linear-gradient(135deg, #0fb87a, #0b96d6)' },
              }}
            >
              Ver produtos
            </Button>
          </Box>
        )}
      </Box>

      {/* Modal do QR Code por canal */}
      <Dialog
        open={Boolean(qrChannel)}
        onClose={() => setQrChannel(null)}
        PaperProps={{
          sx: {
            borderRadius: '22px',
            background: '#ffffff',
            border: `1px solid ${C.border}`,
            color: C.text,
            m: 2,
            width: '100%',
            maxWidth: 380,
            boxShadow: '0 30px 70px rgba(15,23,42,0.28)',
          },
        }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' } }}
      >
        {qrChannel && (
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
              <ChannelAvatar channel={qrChannel} size={48} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: C.strong }}>
                  {qrChannel.name}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: C.muted }}>
                  QR Code do link
                </Typography>
              </Box>
              <IconButton
                onClick={() => setQrChannel(null)}
                aria-label="Fechar"
                sx={{ color: C.muted, '&:hover': { color: C.strong } }}
              >
                <X size={18} />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                mb: 2,
                borderRadius: '18px',
                background: '#ffffff',
                border: `1px solid ${C.border}`,
              }}
            >
              <QrCodeCanvas ref={qrRef} value={qrChannel.url} size={196} ecc="M" />
            </Box>

            <Box
              component="a"
              href={qrChannel.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                mb: 2,
                borderRadius: '12px',
                textDecoration: 'none',
                background: C.inset,
                border: `1px solid ${C.insetBorder}`,
                '&:hover': { borderColor: 'rgba(16,185,129,0.4)' },
              }}
            >
              <Link2 size={14} color={C.accentDeep} style={{ flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.8rem',
                  color: '#334155',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {prettyUrl(qrChannel.url)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <Button
                onClick={handleDownloadQr}
                startIcon={<Download size={18} />}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  borderRadius: '12px',
                  py: 1.1,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #10b981, #0ea5e9)',
                  boxShadow: '0 12px 26px rgba(16,185,129,0.28)',
                  '&:hover': { background: 'linear-gradient(135deg, #0fb87a, #0b96d6)' },
                }}
              >
                Baixar QR Code
              </Button>
              <Button
                onClick={() => handleCopy(qrChannel)}
                startIcon={copiedId === qrChannel.id ? <Check size={18} /> : <Copy size={18} />}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  borderRadius: '12px',
                  py: 1.1,
                  color: C.strong,
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  '&:hover': {
                    background: 'rgba(16,185,129,0.08)',
                    borderColor: 'rgba(16,185,129,0.4)',
                  },
                }}
              >
                {copiedId === qrChannel.id ? 'Copiado' : 'Copiar link'}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default MyLinks;
